import { useDisclosure } from '@heroui/react';
import { toast } from 'react-toastify';
import { useState, useCallback } from 'react';
import { useAtom } from 'jotai';

import { createCdn, updateCdn, deleteCdn, type CdnForm } from '@/store';
import type { CdnItem } from '@/types';

export function useCdnForm() {
  const [, createCdnItem] = useAtom(createCdn);
  const [, updateCdnItem] = useAtom(updateCdn);
  const { isOpen: isFormOpen, onOpen: onFormOpen, onOpenChange: onFormOpenChange } = useDisclosure();
  const [editingCdnId, setEditingCdnId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CdnForm>({
    name: '',
    cdn_tag: 'stable',
    cdn_provider_id: 1,
    version: '',
    filename: '',
    cdn_url: ''
  });

  const openCreate = useCallback(() => {
    setEditingCdnId(null);
    setFormData({
      name: '',
      cdn_tag: 'stable',
      cdn_provider_id: 1,
      version: '',
      filename: '',
      cdn_url: ''
    });
    onFormOpen();
  }, [onFormOpen]);

  const openEdit = useCallback(
    (cdn: CdnItem) => {
      setEditingCdnId(cdn.id);
      setFormData({
        name: cdn.name || '',
        cdn_tag: cdn.cdn_tag,
        cdn_provider_id: cdn.provider?.id || 0,
        version: cdn.version || '',
        filename: cdn.filename || '',
        cdn_url: cdn.cdn_url || ''
      });
      onFormOpen();
    },
    [onFormOpen]
  );

  const saveForm = useCallback(async () => {
    try {
      setIsSubmitting(true);

      if (editingCdnId) {
        // Edit mode
        await updateCdnItem({ id: editingCdnId, formData });
        toast.success('CDN updated successfully');
      } else {
        // Create mode
        await createCdnItem(formData);
        toast.success('CDN created successfully');
      }

      setIsSubmitting(false);
      onFormOpenChange();
      setEditingCdnId(null);
      // Reset form after successful operation
      setFormData({
        name: '',
        cdn_tag: 'stable',
        cdn_provider_id: 1,
        version: '',
        filename: '',
        cdn_url: ''
      });
    } catch (error) {
      setIsSubmitting(false);
      // Error is handled by axios interceptor
    }
  }, [editingCdnId, formData, onFormOpenChange, updateCdnItem, createCdnItem]);

  const isEditMode = editingCdnId !== null;
  const title = isEditMode ? 'Edit CDN' : 'Add CDN';
  const submitText = isEditMode ? 'Update' : 'Create';

  return {
    isFormOpen,
    isSubmitting,
    formData,
    setFormData,
    openCreate,
    openEdit,
    saveForm,
    onFormOpenChange,
    title,
    submitText,
    isEditMode
  };
}

export function useCdnDelete() {
  const [, deleteCdnItem] = useAtom(deleteCdn);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onOpenChange: onDeleteOpenChange } = useDisclosure();
  const [deletingCdnId, setDeletingCdnId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openDelete = useCallback(
    (cdn: CdnItem) => {
      setDeletingCdnId(cdn.id);
      onDeleteOpen();
    },
    [onDeleteOpen]
  );

  const confirmDelete = useCallback(async () => {
    if (!deletingCdnId) return;

    try {
      setIsDeleting(true);
      await deleteCdnItem(deletingCdnId);
      toast.success('CDN deleted successfully');
      setIsDeleting(false);
      onDeleteOpenChange();
      setDeletingCdnId(null);
    } catch (error) {
      setIsDeleting(false);
      // Error is handled by axios interceptor
    }
  }, [deletingCdnId, onDeleteOpenChange, deleteCdnItem]);

  return {
    isDeleteOpen,
    deletingCdnId,
    isDeleting,
    openDelete,
    confirmDelete,
    onDeleteOpenChange
  };
}
